import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Percent, Euro, Store, MapPin, Clock, ShoppingCart, CheckCircle, Ticket } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CouponsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [myCoupons, setMyCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('discover');
  const [purchasing, setPurchasing] = useState(null);
  const token = localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Check for payment verification
  const sessionId = searchParams.get('session_id');
  const couponId = searchParams.get('coupon_id');

  useEffect(() => {
    fetchCoupons();
    if (token) fetchMyCoupons();
    if (sessionId) verifyPurchase();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await fetch(`${API}/coupons/discover`);
      if (res.ok) setCoupons(await res.json());
    } catch { console.error('Failed to load coupons'); }
    finally { setLoading(false); }
  };

  const fetchMyCoupons = async () => {
    try {
      const res = await fetch(`${API}/my-coupons`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setMyCoupons(await res.json());
    } catch {}
  };

  const verifyPurchase = async () => {
    if (!sessionId || !token) return;
    try {
      const res = await fetch(`${API}/coupons/purchase/verify/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.status === 'paid') {
        toast.success(data.message || 'Coupon achete !');
        setTab('my-coupons');
        fetchMyCoupons();
      }
    } catch {}
    // Clean URL params
    window.history.replaceState({}, '', '/coupons');
  };

  const handlePurchase = async (coupon) => {
    if (!token) { navigate('/login'); return; }
    setPurchasing(coupon.id);
    try {
      const formData = new FormData();
      formData.append('origin_url', window.location.origin);
      const res = await fetch(`${API}/coupons/${coupon.id}/purchase`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Erreur de paiement');
      }
    } catch { toast.error('Erreur reseau'); }
    finally { setPurchasing(null); }
  };

  const handleRedeem = async (purchaseId) => {
    try {
      const res = await fetch(`${API}/coupons/redeem/${purchaseId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) { toast.success(data.message); fetchMyCoupons(); }
      else toast.error(data.detail);
    } catch { toast.error('Erreur'); }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copie !');
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3 mb-3">
            <Link to="/"><ArrowLeft className="w-5 h-5 text-charcoal" /></Link>
            <h1 className="font-bold text-charcoal text-lg flex items-center gap-2">
              <Ticket className="w-5 h-5 text-primary" /> Coupons & Bons Plans
            </h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setTab('discover')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'discover' ? 'bg-primary text-white' : 'bg-gray-100 text-charcoal'}`}
              data-testid="coupons-discover-tab">
              Decouvrir
            </button>
            {token && (
              <button onClick={() => setTab('my-coupons')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === 'my-coupons' ? 'bg-primary text-white' : 'bg-gray-100 text-charcoal'}`}
                data-testid="coupons-my-tab">
                Mes coupons ({myCoupons.length})
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {tab === 'discover' && (
          <>
            {coupons.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Tag className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold text-charcoal mb-1">Aucun coupon disponible</h3>
                <p className="text-sm text-muted-foreground">Revenez bientot pour decouvrir les offres des commercants.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {coupons.map(c => (
                  <div key={c.id} className="bg-white rounded-xl overflow-hidden shadow-sm border hover:shadow-md transition-shadow" data-testid={`discover-coupon-${c.id}`}>
                    <div className="bg-gradient-to-r from-primary/10 to-amber-50 p-4">
                      <div className="flex items-center gap-3 mb-2">
                        {c.logo ? (
                          <img src={c.logo} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                            <Store className="w-5 h-5 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-charcoal text-sm">{c.business_name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {c.city}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                          {c.coupon_type === 'percent' ? `-${c.discount_value}%` : `-${c.discount_value} EUR`}
                        </span>
                        {c.min_purchase && (
                          <span className="text-xs text-muted-foreground">des {c.min_purchase} EUR d'achat</span>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-charcoal mb-1">{c.title}</h3>
                      {c.description && <p className="text-sm text-muted-foreground mb-3">{c.description}</p>}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-2xl font-bold text-charcoal">{c.price} EUR</span>
                          <span className="text-xs text-muted-foreground ml-2 flex items-center gap-1 inline-flex">
                            <Clock className="w-3 h-3" /> Jusqu'au {c.valid_until}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">{c.remaining} restant{c.remaining > 1 ? 's' : ''}</div>
                      </div>
                      <Button className="w-full mt-3" onClick={() => handlePurchase(c)}
                        disabled={purchasing === c.id} data-testid={`buy-coupon-${c.id}`}>
                        {purchasing === c.id ? (
                          <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                        ) : (
                          <ShoppingCart className="w-4 h-4 mr-2" />
                        )}
                        Acheter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === 'my-coupons' && (
          <>
            {myCoupons.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center shadow-sm">
                <Ticket className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-semibold text-charcoal mb-1">Aucun coupon achete</h3>
                <p className="text-sm text-muted-foreground mb-3">Decouvrez les offres des commercants autour de vous !</p>
                <Button onClick={() => setTab('discover')}>Voir les coupons</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {myCoupons.map(p => (
                  <div key={p.id} className={`bg-white rounded-xl p-4 shadow-sm border-l-4 ${p.is_redeemed ? 'border-l-gray-300 opacity-60' : 'border-l-green-500'}`}
                    data-testid={`my-coupon-${p.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-semibold text-charcoal">{p.coupon_title}</h3>
                        <p className="text-sm text-muted-foreground">{p.business_name}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${p.is_redeemed ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                        {p.is_redeemed ? 'Utilise' : 'Valide'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mb-3">
                      <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-bold">
                        {p.coupon_type === 'percent' ? `-${p.discount_value}%` : `-${p.discount_value} EUR`}
                      </span>
                      <span className="text-xs text-muted-foreground">Valide jusqu'au {p.valid_until}</span>
                    </div>
                    {!p.is_redeemed && (
                      <div className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Votre code</p>
                          <p className="font-mono font-bold text-charcoal text-lg">{p.redeem_code}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" onClick={() => copyCode(p.redeem_code)}>
                            Copier
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleRedeem(p.id)}>
                            Marquer utilise
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
